/**
 * Feature Flags Admin Page
 *
 * Admin interface for feature flag management with metrics dashboard.
 * Provides controls for toggling flags and monitoring rollout progress.
 *
 * Features:
 * - Page layout following existing admin patterns
 * - FeatureFlagPanel component for flag management
 * - Metrics section showing rollout progress
 * - Rollback guide for instant rollback
 * - Access control for admin users only
 * - Breadcrumb navigation: Admin > Feature Flags
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { AdminGuard } from '@/components/admin/AdminGuard';
import FeatureFlagPanel from '@/components/admin/FeatureFlagPanel';
import { useToast } from '@/components/ui/use-toast';
import {
  useFeatureFlagMetrics,
  useColorRolloutProgress,
  useColorRolloutManager,
  useColorSchemeFeedback,
  usePostLaunchMetrics,
  captureBaselineMetrics,
  compareMetrics,
  recordLaunchTimestamp,
  type FeatureFlagAuditEntry,
  type BaselineMetrics,
  type MetricsComparison,
} from '@/hooks/useFeatureFlagMetrics';
import {
  Settings,
  Users,
  TrendingUp,
  History,
  Info,
  RefreshCw,
  ArrowLeft,
  Activity,
  AlertTriangle,
  MessageSquare,
  Star,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Rollback thresholds for visual comparison
const THRESHOLDS = {
  engagementWarning: -5,
  engagementRollback: -10,
  errorRateWarning: 10,
  errorRateRollback: 20,
  ticketWarning: 25,
  ticketRollback: 50,
} as const;

export default function FeatureFlagsPage() {
  const navigate = useNavigate();
  const { metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useFeatureFlagMetrics();
  const { progress } = useColorRolloutProgress();
  const { setRolloutPercentage, enableFullRollout, rollback, expandRollout, isUpdating: isRolloutUpdating } = useColorRolloutManager();
  const { stats: feedbackStats, isLoading: feedbackLoading, refetch: refetchFeedback } = useColorSchemeFeedback();
  const { metrics: postLaunchMetrics, isLoading: postLaunchLoading } = usePostLaunchMetrics();
  const { toast } = useToast();
  const [isRefetching, setIsRefetching] = useState(false);
  const [baselineCaptured, setBaselineCaptured] = useState(false);

  const handleCaptureBaseline = () => {
    const baseline = captureBaselineMetrics();
    setBaselineCaptured(true);
    refetchMetrics();
  };

  const handleSetRollout = async (percentage: number) => {
    // Use expandRollout for validation if increasing percentage
    const currentPercentage = metrics?.rolloutPercentage || 0;

    if (percentage > currentPercentage) {
      // Expanding - use validated expansion
      const result = await expandRollout(percentage);

      if (result.success) {
        toast({
          title: 'Rollout expandido',
          description: result.message,
        });
        refetchMetrics();
      } else {
        toast({
          title: 'Expansão não permitida',
          description: result.message,
          variant: 'destructive',
        });
      }
    } else {
      // Decreasing or same - direct update
      const success = await setRolloutPercentage(percentage);
      if (success) {
        toast({
          title: 'Rollout atualizado',
          description: `Porcentagem de rollout definida para ${percentage}%`,
        });
        refetchMetrics();
      } else {
        toast({
          title: 'Erro',
          description: 'Não foi possível atualizar o rollout',
          variant: 'destructive',
        });
      }
    }
  };

  const handleEnableFull = async () => {
    const success = await enableFullRollout();
    if (success) {
      toast({
        title: 'Rollout completo',
        description: 'Feature flag ativada para 100% dos usuários',
      });
      refetchMetrics();
    } else {
      toast({
        title: 'Erro',
        description: 'Não foi possível ativar a feature flag',
        variant: 'destructive',
      });
    }
  };

  const handleRollback = async () => {
    const success = await rollback();
    if (success) {
      toast({
        title: 'Rollback executado',
        description: 'Feature flag desativada e rollback para 0%',
      });
      refetchMetrics();
    } else {
      toast({
        title: 'Erro',
        description: 'Não foi possível executar o rollback',
        variant: 'destructive',
      });
    }
  };

  const handleRefresh = async () => {
    setIsRefetching(true);
    await Promise.all([refetchMetrics(), refetchFeedback()]);
    setTimeout(() => setIsRefetching(false), 500);
  };

  const handleFlagsUpdated = () => {
    // Refresh metrics when flags are updated
    refetchMetrics();
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return 'desconhecido';
    }
  };

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'enabled':
        return <Badge variant="default" className="text-xs">Ativada</Badge>;
      case 'disabled':
        return <Badge variant="secondary" className="text-xs">Desativada</Badge>;
      case 'percentage_changed':
        return <Badge variant="outline" className="text-xs">Porcentagem</Badge>;
      case 'created':
        return <Badge variant="default" className="text-xs bg-green-600">Criada</Badge>;
      case 'deleted':
        return <Badge variant="destructive" className="text-xs">Excluída</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{action}</Badge>;
    }
  };

  return (
    <AdminGuard>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="hover:bg-transparent p-0 h-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Voltar
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-foreground">Feature Flags</h1>
            <p className="text-muted-foreground">
              Gerencie recursos em rollout e monitore o progresso de implantação
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefetching || metricsLoading || feedbackLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
          <span>Admin</span>
          <span>/</span>
          <span className="text-foreground font-medium">Feature Flags</span>
        </nav>

        <Separator />

        {/* Rollback Guide */}
        <Alert className="border-amber-200 bg-amber-50">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Rollback instantâneo:</strong> Para reverter qualquer feature, desative a flag correspondente.
            As alterações entram em vigor imediatamente no próximo carregamento de página dos usuários.
          </AlertDescription>
        </Alert>

        {/* Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Users Tracked */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Usuários Rastreados</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metricsLoading ? '-' : metrics?.totalUsers || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Usuários com variante de cor rastreada
              </p>
            </CardContent>
          </Card>

          {/* Legacy Variant Users */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Variante Legacy</CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metricsLoading ? '-' : metrics?.legacyCount || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {metricsLoading ? '-' : `${metrics?.refinedPercentage || 0}% em Refined`}
              </p>
            </CardContent>
          </Card>

          {/* Refined Variant Users */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Variante Refined</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metricsLoading ? '-' : metrics?.refinedCount || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {metricsLoading ? '-' : `${metrics?.refinedPercentage || 0}% do total`}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Current Flag Status */}
        <Card>
          <CardHeader>
            <CardTitle>Status Atual</CardTitle>
            <CardDescription>
              Estado atual da flag color_refined e porcentagem de rollout configurada
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">color_refined</span>
                  {metrics?.isColorRefinedEnabled ? (
                    <Badge variant="default" className="text-xs">Ativa (100%)</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      Rollout: {metrics?.rolloutPercentage || 0}%
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {metrics?.isColorRefinedEnabled
                    ? 'Todos os usuários verão o tema refinado'
                    : `Usuários veem o tema baseado em hash de ID + porcentagem de rollout`
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions - Rollout Controls */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Acoes Rapidas de Rollout
            </CardTitle>
            <CardDescription>
              Ajuste a porcentagem de rollout para o tema refinado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Rollout Percentage Buttons */}
              <div className="grid grid-cols-4 gap-3">
                <Button
                  onClick={() => handleSetRollout(5)}
                  disabled={isRolloutUpdating || metrics?.rolloutPercentage === 5}
                  variant={metrics?.rolloutPercentage === 5 ? "default" : "outline"}
                  className="relative"
                >
                  5%
                  {metrics?.rolloutPercentage === 5 && (
                    <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                  )}
                </Button>
                <Button
                  onClick={() => handleSetRollout(25)}
                  disabled={isRolloutUpdating || metrics?.rolloutPercentage === 25}
                  variant={metrics?.rolloutPercentage === 25 ? "default" : "outline"}
                  className="relative"
                >
                  25%
                  {metrics?.rolloutPercentage === 25 && (
                    <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                  )}
                </Button>
                <Button
                  onClick={() => handleSetRollout(50)}
                  disabled={isRolloutUpdating || metrics?.rolloutPercentage === 50}
                  variant={metrics?.rolloutPercentage === 50 ? "default" : "outline"}
                  className="relative"
                >
                  50%
                  {metrics?.rolloutPercentage === 50 && (
                    <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                  )}
                </Button>
                <Button
                  onClick={() => handleSetRollout(100)}
                  disabled={isRolloutUpdating || metrics?.rolloutPercentage === 100}
                  variant={metrics?.rolloutPercentage === 100 ? "default" : "outline"}
                  className="relative"
                >
                  100%
                  {metrics?.rolloutPercentage === 100 && (
                    <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                  )}
                </Button>
              </div>

              {/* Advanced Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleEnableFull}
                  disabled={isRolloutUpdating || metrics?.isColorRefinedEnabled}
                  variant="default"
                  className="flex-1"
                >
                  {isRolloutUpdating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Atualizando...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Ativar 100% (Flag)
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleRollback}
                  disabled={isRolloutUpdating || (metrics?.rolloutPercentage === 0 && !metrics?.isColorRefinedEnabled)}
                  variant="destructive"
                  className="flex-1"
                >
                  {isRolloutUpdating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Atualizando...
                    </>
                  ) : (
                    <>
                      <History className="h-4 w-4 mr-2" />
                      Rollback (0%)
                    </>
                  )}
                </Button>
              </div>

              {/* Current Status Display */}
              <div className="text-sm text-muted-foreground text-center pt-2">
                {metrics?.isColorRefinedEnabled ? (
                  <span className="text-green-600 font-medium">
                    Flag ativada - 100% dos usuarios veem o tema refinado
                  </span>
                ) : (
                  <span>
                    Rollout gradual ativo: <span className="font-bold">{metrics?.rolloutPercentage || 0}%</span> dos usuarios
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Baseline Metrics */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Métricas de Base
                </CardTitle>
                <CardDescription>
                  Métricas capturadas antes do rollout para comparação
                </CardDescription>
              </div>
              {!metrics?.baseline && (
                <Button
                  onClick={handleCaptureBaseline}
                  size="sm"
                  variant="outline"
                >
                  Capturar Base
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {metrics?.baseline ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Capturado em:</span>
                  <span className="font-medium">
                    {formatDate(metrics.baseline.timestamp)}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Sessão Média</p>
                    <p className="text-lg font-semibold">{metrics.baseline.avgSessionDuration}s</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Usuários Ativos</p>
                    <p className="text-lg font-semibold">{metrics.baseline.dailyActiveUsers}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Taxa de Erro</p>
                    <p className="text-lg font-semibold">{metrics.baseline.errorRate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Tickets/Dia</p>
                    <p className="text-lg font-semibold">{metrics.baseline.supportTicketsPerDay}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground text-sm">
                Nenhuma métrica de base capturada ainda.
                <br />
                <span className="text-xs">Capture antes de iniciar o rollout.</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metrics Comparison (if baseline exists) */}
        {metrics?.comparison && metrics.baseline && (
          <Card className={
            metrics.comparison.status === 'critical' ? 'border-red-300 bg-red-50' :
            metrics.comparison.status === 'warning' ? 'border-amber-300 bg-amber-50' :
            'border-green-300 bg-green-50'
          }>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {metrics.comparison.status === 'critical' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                {metrics.comparison.status === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-600" />}
                Comparação com Base
              </CardTitle>
              <CardDescription>
                {metrics.comparison.status === 'critical' && 'Alguns thresholds de rollback foram excedidos!'}
                {metrics.comparison.status === 'warning' && 'Algumas métricas precisam de atenção.'}
                {metrics.comparison.status === 'healthy' && 'Todas as métricas dentro dos limites aceitáveis.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Engajamento</p>
                    <p className={`text-lg font-semibold ${
                      metrics.comparison.engagementChange < THRESHOLDS.engagementWarning ? 'text-red-600' :
                      metrics.comparison.engagementChange < 0 ? 'text-amber-600' :
                      'text-green-600'
                    }`}>
                      {metrics.comparison.engagementChange > 0 ? '+' : ''}{metrics.comparison.engagementChange}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Taxa de Erro</p>
                    <p className={`text-lg font-semibold ${
                      metrics.comparison.errorRateChange > THRESHOLDS.errorRateWarning ? 'text-red-600' :
                      metrics.comparison.errorRateChange > 0 ? 'text-amber-600' :
                      'text-green-600'
                    }`}>
                      {metrics.comparison.errorRateChange > 0 ? '+' : ''}{metrics.comparison.errorRateChange}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Tickets</p>
                    <p className={`text-lg font-semibold ${
                      metrics.comparison.ticketChange > THRESHOLDS.ticketWarning ? 'text-red-600' :
                      metrics.comparison.ticketChange > 0 ? 'text-amber-600' :
                      'text-green-600'
                    }`}>
                      {metrics.comparison.ticketChange > 0 ? '+' : ''}{metrics.comparison.ticketChange}%
                    </p>
                  </div>
                </div>
                {metrics.comparison.alerts.length > 0 && (
                  <Alert className={
                    metrics.comparison.status === 'critical' ? 'border-red-300 bg-red-100' :
                    'border-amber-300 bg-amber-100'
                  }>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      <strong>Alertas:</strong>
                      <ul className="list-disc list-inside mt-1">
                        {metrics.comparison.alerts.map((alert, i) => (
                          <li key={i}>{alert}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feedback Summary */}
        {feedbackStats && feedbackStats.total > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Resumo de Feedback
              </CardTitle>
              <CardDescription>
                Opiniao dos usuarios sobre o esquema de cores
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Sentiment Distribution */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{feedbackStats.positive}</p>
                    <p className="text-xs text-muted-foreground">Positivos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{feedbackStats.neutral}</p>
                    <p className="text-xs text-muted-foreground">Neutros</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{feedbackStats.negative}</p>
                    <p className="text-xs text-muted-foreground">Negativos</p>
                  </div>
                </div>

                {/* Overall Rating */}
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Avaliacao Media</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-2xl font-bold">{feedbackStats.averageRating}</span>
                      <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                      <span className="text-sm text-muted-foreground">
                        ({feedbackStats.total} avaliacoes)
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Feedback Positivo</p>
                    <p className="text-xl font-semibold text-green-600">
                      {feedbackStats.positivePercentage}%
                    </p>
                  </div>
                </div>

                {/* By Variant */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Variante Legacy</p>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{feedbackStats.byVariant.legacy.total} feedbacks</span>
                      <span className="text-sm">
                        <Star className="h-4 w-4 inline fill-amber-400 text-amber-400" />
                        {' '}{feedbackStats.byVariant.legacy.averageRating}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Variante Refined</p>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{feedbackStats.byVariant.refined.total} feedbacks</span>
                      <span className="text-sm">
                        <Star className="h-4 w-4 inline fill-amber-400 text-amber-400" />
                        {' '}{feedbackStats.byVariant.refined.averageRating}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Recent Comments */}
                {feedbackStats.recentComments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Comentarios Recentes</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {feedbackStats.recentComments.map((comment) => (
                        <div key={comment.id} className="p-2 bg-muted/50 rounded text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium capitalize">
                              {comment.variant === 'refined' ? 'Refinado' : 'Legado'}
                            </span>
                            <div className="flex items-center gap-1">
                              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                              <span className="text-xs">{comment.rating}</span>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {comment.comment}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Post-Launch Monitoring (shown when rollout >= 50%) */}
        {metrics && metrics.rolloutPercentage >= 50 && postLaunchMetrics && (
          <Card className={
            postLaunchMetrics.status === 'rollback' ? 'border-red-300 bg-red-50' :
            postLaunchMetrics.status === 'success' ? 'border-green-300 bg-green-50' :
            'border-amber-300 bg-amber-50'
          }>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Monitoramento Pós-Lançamento
              </CardTitle>
              <CardDescription>
                Métricas dos primeiros 7 dias após rollout de 100%
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Status Indicator */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Status v1.2</p>
                    <div className="flex items-center gap-2 mt-1">
                      {postLaunchMetrics.status === 'success' && (
                        <span className="text-lg font-semibold text-green-600">
                          Rollout Bem-Sucedido
                        </span>
                      )}
                      {postLaunchMetrics.status === 'monitoring' && (
                        <span className="text-lg font-semibold text-amber-600">
                          Em Monitoramento
                        </span>
                      )}
                      {postLaunchMetrics.status === 'rollback' && (
                        <span className="text-lg font-semibold text-red-600">
                          Requer Atenção
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Dias até estabilidade</p>
                    <p className="text-lg font-semibold">
                      {postLaunchMetrics.daysUntilStable > 0
                        ? `${postLaunchMetrics.daysUntilStable} dias`
                        : 'Estável'}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Progresso da Semana 1</span>
                    <span>{Math.min(7, postLaunchMetrics.daysSinceLaunch)}/7 dias</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-500"
                      style={{ width: `${Math.min(100, (postLaunchMetrics.daysSinceLaunch / 7) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Daily Metrics Summary */}
                <div className="grid grid-cols-3 gap-4 pt-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Engajamento</p>
                    <p className="text-lg font-semibold">
                      {postLaunchMetrics.dailyMetrics.length > 0
                        ? `${postLaunchMetrics.dailyMetrics[postLaunchMetrics.dailyMetrics.length - 1]?.engagement || 100}%`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Taxa de Erro</p>
                    <p className="text-lg font-semibold">
                      {postLaunchMetrics.dailyMetrics.length > 0
                        ? `${postLaunchMetrics.dailyMetrics[postLaunchMetrics.dailyMetrics.length - 1]?.errorRate || 2}%`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Feedback Total</p>
                    <p className="text-lg font-semibold">
                      {postLaunchMetrics.cumulativeFeedback.total}
                    </p>
                  </div>
                </div>

                {/* Cumulative Feedback */}
                {postLaunchMetrics.cumulativeFeedback.total > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground mb-2">Feedback Acumulado</p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="font-medium">{postLaunchMetrics.cumulativeFeedback.averageRating}</span>
                      </div>
                      <div className="flex gap-2 text-sm">
                        <span className="text-green-600">+{postLaunchMetrics.cumulativeFeedback.positive}</span>
                        <span className="text-amber-600">{postLaunchMetrics.cumulativeFeedback.neutral}</span>
                        <span className="text-red-600">-{postLaunchMetrics.cumulativeFeedback.negative}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Changes Timeline */}
        {(metrics?.recentChanges && metrics.recentChanges.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Alterações Recentes
              </CardTitle>
              <CardDescription>
                Histórico de mudanças nas feature flags
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {metrics.recentChanges.map((entry: FeatureFlagAuditEntry) => (
                  <div key={entry.id} className="flex items-start gap-3 text-sm">
                    <div className="flex flex-col items-center">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                      <div className="w-px h-full bg-border min-h-[2rem]" />
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2">
                        {getActionBadge(entry.action)}
                        <span className="font-medium">{entry.flag_name}</span>
                        <span className="text-muted-foreground">
                          {formatDate(entry.created_at)}
                        </span>
                      </div>
                      {(entry.old_value && Object.keys(entry.old_value).length > 0) && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {entry.action === 'percentage_changed' && (
                            <span>
                              {entry.old_value.rollout_percentage}% → {entry.new_value.rollout_percentage}%
                            </span>
                          )}
                          {entry.action === 'enabled' && (
                            <span>Ativada</span>
                          )}
                          {entry.action === 'disabled' && (
                            <span>Desativada</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feature Flag Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Gerenciar Flags</CardTitle>
            <CardDescription>
              Alterne features e ajuste porcentagens de rollout
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FeatureFlagPanel
              onFlagsUpdated={handleFlagsUpdated}
              totalUserCount={metrics?.totalUsers || 1000}
            />
          </CardContent>
        </Card>
      </div>
    </AdminGuard>
  );
}

// Export for testing
export { FeatureFlagsPage };
